import { ATTR_HTTP_ROUTE } from '@opentelemetry/semantic-conventions';
import * as Sentry from '@sentry/nextjs';
import Bubble from 'components/bubble';
import Button from 'components/buttons/button';
import CTA from 'components/Campaign/CTA';
import { CAMPAIGN_TIERS } from 'components/Campaign/config';
import { JsonLd } from 'components/jsonLd';
import Layout, { DATA_TYPES } from 'components/layout';
import ProgressBar from 'components/progressBar';
import PromoteContent from 'components/promote';
import ShareBlock from 'components/Sharing';
import Anchor from 'components/typography/anchor';
import petitionShareImg from 'images/shares/trending.png';
import votizen from 'images/votizen.svg';
import { getBillLink } from 'lib/api/billLinks';
import {
  getPetition,
  getTrendingPetitionsV2,
  getUser,
} from 'lib/api/themis/queries';
import { otelApi } from 'lib/instrumentation'; // Initializes instrumentation
import {
  fieldsFullPetition,
  PETITION_SIGNING_STATES,
  trimPetitions,
} from 'lib/petitions';
import Head from 'next/head';
import Img from 'next/image';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { QRCodeSVG } from 'qrcode.react';
import config, { DATA_TYPES as CONTENT_TYPES } from 'siteconfig';
import { getOpenGraphUrl } from 'utilities/paths';
import { PetitionType } from 'utilities/propTypes';
import { getStateAPAbbreviation } from 'utilities/states';
import getTargetTypeHumanName from 'utilities/targets';
import { buildPetitionMetaDescription } from 'utilities/textPresentation';

const REVALIDATE_TIME_MINUTES = 10;
const REVALIDATE_TIME_SECONDS_FIVE_MINUTES = REVALIDATE_TIME_MINUTES * 60;

const currentYear = new Date().getFullYear();

export const getStaticProps = Sentry.wrapGetStaticPropsWithSentry(
  async ({ params }) => {
    const parentCtx = otelApi.context.active();
    const parentSpan = otelApi.trace
      .getTracer('Gin Rickey')
      .startSpan('petition-detail', undefined, parentCtx);

    const shareCode = params.sharecode.toUpperCase();
    parentSpan.setAttributes({
      [ATTR_HTTP_ROUTE]: `/petitions/${shareCode}`,
    });

    // If the case was wrong on the sharecode, redirect to uppercased version
    // This should help SEO and reduce people sharing the wrong one
    if (shareCode !== params.sharecode) {
      return {
        redirect: {
          destination: `/petitions/${shareCode}`,
          permanent: true,
        },
      };
    }

    const [petition, trendingPetitions, amendmentBill] = await Promise.all([
      getPetition({ shareCode }),
      getTrendingPetitionsV2({
        range: 'SEVEN_DAY',
        limit: 12,
      }),
      getBillLink(shareCode),
    ]);

    if (!petition) {
      return {
        notFound: true,
        revalidate: REVALIDATE_TIME_SECONDS_FIVE_MINUTES,
      };
    }
    const user = await getUser({ id: petition.userId });

    if (user?.code) {
      petition.petitioner_code = user.code;
    }

    // Fetch petition data
    const isTrending = trendingPetitions.filter(
      (p) => p.petition_code === shareCode
    );

    // Move this to secondary level for if we ever use different time periods
    // This also happens in packages/site/src/lib/api/petitions/index.js
    petition.trending = {
      sign_count_period: isTrending[0]?.trending?.sign_count_period || null,
    };

    const slimPetition = trimPetitions({
      petitions: [petition],
      fieldsToKeep: fieldsFullPetition,
    })[0];

    parentSpan.end();
    return {
      props: {
        petition: slimPetition,
        amendmentBill: amendmentBill || null,
        promoted: {
          // We now allow it on all content. We used to prevent it while logged in, or on unapproved petitions
          canPromote: !process.env.NEXT_PUBLIC_DISABLE_PROMOTES,
        },
      },
      // Want people to be able to see the page update relatively frequently
      revalidate: REVALIDATE_TIME_SECONDS_FIVE_MINUTES,
    };
  }
);

// Don't pre-render anything
export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

const PetitionMetadata = ({ dateString, petition, userCode }) => {
  const name = userCode ? (
    <Anchor className="anchor-text" href={`/go/${userCode.toLowerCase()}`}>
      {petition.petitioner_name}
    </Anchor>
  ) : (
    petition.petitioner_name
  );

  return (
    <p className="print:hidden">
      &#9654; Created <em>on</em> {dateString} <em>by</em> {name}{' '}
      {petition.trending.sign_count_period
        ? `· ${petition.trending.sign_count_period.toLocaleString()} signers in
            the past 7 days`
        : null}
    </p>
  );
};

const Petition = ({ petition, promoted, amendmentBill }) => {
  const router = useRouter();
  const url = router.asPath;

  const hasState = !!petition.state;
  const hasCity = !!petition.city;
  let locationDetail = '';
  const targetDetail = getTargetTypeHumanName(petition.target);

  // Location detail needs to handle city or state filters
  if (hasState && hasCity) {
    locationDetail = ` (${petition.city}, ${getStateAPAbbreviation(
      petition.state
    )} only)`;
  } else if (hasState && !hasCity) {
    locationDetail = ` (${getStateAPAbbreviation(petition.state)} only)`;
  }

  // JS uses millis based timestamp, but the API returns seconds
  const date = new Date(petition.sent_timestamp * 1000);
  const isThisYear = date.getFullYear() === currentYear;
  const dateString = isThisYear
    ? // October 14
      `${new Intl.DateTimeFormat('en-US', { month: 'long' }).format(
        date
      )} ${date.getDate()}`
    : // October 14, 1983
      `${new Intl.DateTimeFormat('en-US', { month: 'long' }).format(
        date
      )} ${date.getDate()}, ${date.getFullYear()}`;
  const subject = petition.subject
    ? petition.subject
    : petition.message.substring(0, 79);
  const is_closed = petition.sign_status === PETITION_SIGNING_STATES.CLOSED;

  // Dynamic OG image
  const shareCode = petition.petition_code;
  const target = petition.target;
  const city = petition.city || '';
  const state = petition.state || '';

  const dynamicOpenGraphUrlHref = getOpenGraphUrl('/api/og/petition/petition', {
    title: subject,
    shareCode: shareCode,
    target: target,
    city: city,
    state: state,
  });

  const imageAltText = `A shareable card that reads "tell ${targetDetail}${
    locationDetail ? `${locationDetail}: ` : ': '
  }${subject}" followed by "text sign ${petition.petition_code} to 50409"`;

  const metaDescription = buildPetitionMetaDescription({
    subject: petition.subject,
    message: petition.message,
    fallback: `Sign this petition and send a letter to ${targetDetail} in minutes.`,
  });

  return (
    <Layout
      title={`${subject} — Sign & Send to ${targetDetail} | Resistbot`}
      ogTitle={subject}
      dataType={DATA_TYPES.petition}
      description={metaDescription}
      image={petitionShareImg}
      dynamicImage={dynamicOpenGraphUrlHref}
      imageWidth={1200}
      imageHeight={630}
      imageAltText={imageAltText}
      canonicalPath={`/petitions/${petition.petition_code}`}
    >
      <Head>
        <JsonLd
          data={{
            '@context': 'https://schema.org',
            '@type': 'CreativeWork',
            name: subject,
            description: metaDescription,
            url: `${config.siteUrl}/petitions/${petition.petition_code}`,
            dateCreated: new Date(petition.sent_timestamp * 1000).toISOString(),
            author: {
              '@type': 'Person',
              name: petition.petitioner_name,
            },
            publisher: {
              '@type': 'Organization',
              name: 'Resistbot',
              url: 'https://resist.bot',
            },
            interactionStatistic: {
              '@type': 'InteractionCounter',
              interactionType: 'https://schema.org/EndorseAction',
              userInteractionCount: petition.sign_count,
            },
          }}
        />
      </Head>
      <div className="mt-4 mb-8 sm:text-center">
        <p
          className="text-sm tracking-wider uppercase"
          data-test-id={'heading'}
        >
          An open letter <em className="lowercase">to</em> {targetDetail}{' '}
          {locationDetail}
        </p>
        <h1 className="my-4 text-3xl">{subject}</h1>
        {!is_closed ? (
          <ProgressBar
            current={petition.sign_count}
            tiers={CAMPAIGN_TIERS}
            label={'signers'}
          />
        ) : null}
      </div>

      <div className="flex flex-wrap gap-10 sm:flex-nowrap">
        <div className="max-w-full grow">
          <div className="mb-7 max-w-prose font-serif leading-relaxed text-pretty break-words hyphens-auto whitespace-pre-line font-optical-expanded print:mb-0 print:line-clamp-20 print:max-w-full print:leading-5">
            {petition.message}
          </div>

          {amendmentBill ? (
            <div className="mb-7 flex max-w-prose items-center gap-4 rounded-lg border border-stone-200 bg-stone-50 p-4 text-sm dark:border-stone-700 dark:bg-stone-800/50">
              <Img
                src={votizen}
                alt=""
                width={36}
                height={36}
                className="shrink-0"
              />
              <div>
                {amendmentBill.ask ? (
                  <p>
                    Want candidates running for office to hear where you stand?{' '}
                    <Anchor
                      className="anchor-text"
                      href={amendmentBill.ask.url}
                    >
                      {amendmentBill.ask.label} on Amendment →
                    </Anchor>
                  </p>
                ) : (
                  <p>
                    Want the full legislative picture with sponsors, status, and
                    votes?{' '}
                    <Anchor className="anchor-text" href={amendmentBill.url}>
                      Track {amendmentBill.bill} on Amendment →
                    </Anchor>
                  </p>
                )}
              </div>
            </div>
          ) : null}

          <PetitionMetadata
            dateString={dateString}
            petition={petition}
            userCode={petition.petitioner_code}
          />

          <div className="hidden text-center print:block">
            <h2 className="mt-10 mb-4">
              Text SIGN {petition.petition_code} to 50409
            </h2>
            <QRCodeSVG
              className="mx-auto"
              size={128}
              bgColor={'transparent'}
              value={`smsto:50409?&body=SIGN%20${petition.petition_code}`}
            />
          </div>
        </div>

        <div className="grow basis-80 print:hidden">
          <CTA
            shareCode={petition.petition_code}
            type={CONTENT_TYPES.PETITION}
            isClosed={is_closed}
          />

          <h2>Already signed?</h2>

          <ul className="my-4 list-disc space-y-4 text-sm text-stone-500 marker:text-stone-300 dark:text-stone-400 dark:marker:text-stone-600">
            {promoted.canPromote ? (
              // A link to the form at the bottom of the page
              <li>
                <Anchor className="anchor-text" href="#promote-form">
                  Promote this campaign
                </Anchor>{' '}
                to get it texted to potential&nbsp;signers
              </li>
            ) : (
              // Jsut instruct the user how to promote in-bot
              <li>
                Text <Bubble>PROMOTE {petition.petition_code}</Bubble> to get
                this texted to more members
              </li>
            )}
            <li>
              Share this page or{' '}
              <Anchor className="anchor-text" href={dynamicOpenGraphUrlHref}>
                image
              </Anchor>
              <Anchor href={dynamicOpenGraphUrlHref}>
                <div className="relative mt-2 aspect-1200/630 w-full max-w-80 border-4 dark:border-stone-700">
                  <Img src={dynamicOpenGraphUrlHref} fill alt={imageAltText} />
                </div>
              </Anchor>
            </li>
            <li>
              Text <Bubble>INVITE {petition.petition_code}</Bubble> to ask your
              friends to sign via text or email
            </li>
            <li>
              <Button theme="link" onClick={() => window.print()}>
                Print this
              </Button>{' '}
              and post around campus or on your community bulletin&nbsp;board
            </li>
            <li>
              Use the{' '}
              <Anchor className="anchor-text" href={config.miscUrls.iosApp}>
                iOS app
              </Anchor>{' '}
              to share with your&nbsp;contacts
            </li>
            <li>
              Join our{' '}
              <Anchor className="anchor-text" href={config.socialUrls.discord}>
                Discord
              </Anchor>{' '}
              and connect with fellow organizers
            </li>
            <li>
              <Anchor className="anchor-text" href="/premium">
                Upgrade to Premium
              </Anchor>{' '}
              to unlock more features and make sure we can keep delivering
            </li>
          </ul>

          <ShareBlock
            pathname={url}
            text={`Text SIGN ${petition.petition_code} to 50409`}
          />
        </div>
      </div>
      {/* Promotion CTA: Don't show to a logged in user, unapproved petitions, or if it's globally disabled */}
      {promoted.canPromote && (
        <PromoteContent
          shareCode={petition.petition_code}
          type={CONTENT_TYPES.PETITION.typeName}
        />
      )}
    </Layout>
  );
};

Petition.propTypes = {
  petition: PetitionType,
  promoted: PropTypes.shape({
    canPromote: PropTypes.bool,
  }),
  amendmentBill: PropTypes.shape({
    // Null on ask-only entries — the ask branch renders instead of the bill row
    url: PropTypes.string,
    bill: PropTypes.string,
    ask: PropTypes.shape({
      url: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  }),
};

export default Petition;
